import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Toaster, toast } from "sonner";
import {
  MessageCircle,
  Phone,
  Check,
  Star,
  Plus,
  Minus,
  ArrowRight,
} from "lucide-react";
import { COMPANY } from "../content";
import usePageMeta from "../hooks/usePageMeta";
import { videoJsonLd } from "../lib/videoSchema";
import useHashScroll from "../hooks/useHashScroll";
import Breadcrumbs from "../components/Breadcrumbs";
import QuickQuoteStrip from "../components/QuickQuoteStrip";
import AISOBlock from "../components/AISOBlock";
import { submitInquiry } from "../lib/api";
import { track } from "../lib/analytics";
import MotionMedia from "../components/MotionMedia";
import CinematicServiceHero from "../components/CinematicServiceHero";
import Reveal from "../components/Reveal";
import { STYLE_GUIDES, STYLE_GROUPS, GUIDE_BY_SLUG } from "../data/curtainStyleGuides";
import { getMedia } from "../data/mediaRegistry";
import {
  PRICING_TIERS,
  CURTAIN_TYPES,
  BLINDS,
  WHY_CHOOSE,
  FAQS,
  INITIAL_FORM,
} from "../data/curtainsData";

const PRODUCT_TYPE_OPTIONS = [
  "Ripple Fold",
  "M-Track",
  "Pinch Pleat",
  "Two Pleat (Double Pinch)",
  "Blackout",
  "Sheer",
  "Double Sheer",
  "Double Layer",
  "Roller Blinds",
  "Zebra Blinds",
  "Roman Blinds",
  "Vertical Blinds",
  "Motorized",
  "Not sure",
];

const NAME_TO_SLUG = {
  "Ripple Fold Curtains": "ripple-fold",
  "M-Track Curtains": "m-track",
  "Pinch Pleat Curtains": "pinch-pleat",
  "Blackout Curtains": "blackout",
  "Sheer Curtains": "sheer",
  "Double Layer Curtains": "double-layer",
  "Roller Blinds": "roller",
  "Zebra Blinds": "zebra",
  "Roman Blinds": "roman",
  "Vertical Blinds": "vertical",
};

export default function CurtainsDubai() {
  const [form, setForm] = useState(INITIAL_FORM);
  const [loading, setLoading] = useState(false);
  const [openFaq, setOpenFaq] = useState(0);
  const [showDetails, setShowDetails] = useState(false);
  const location = useLocation();
  useHashScroll();

  // Style-guide CTA carry-through: prefill product type only when it matches
  // a known option; ordinary visitors keep the "Not sure" default.
  useEffect(() => {
    const style = new URLSearchParams(location.search).get("style");
    if (style && PRODUCT_TYPE_OPTIONS.includes(style) && style !== "Not sure") {
      setForm((f) => ({ ...f, curtainStyle: style }));
      setShowDetails(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone) {
      toast.error("Please share your name and phone number.");
      return;
    }
    setLoading(true);
    try {
      const knowsSizes = form.knowsSize === "I know my sizes";
      const composed = [
        `Type: ${form.type}`,
        `Style: ${form.curtainStyle}`,
        `Property: ${form.propertyType}`,
        `Windows: ${form.numWindows || "Not provided"}`,
        `Measurement: ${form.knowsSize}`,
        knowsSizes && (form.height || form.width)
          ? `Dimensions: ${form.width || "?"} W × ${form.height || "?"} H`
          : null,
        `Automation: ${form.automation}`,
        form.message ? `\nMessage:\n${form.message}` : "",
      ]
        .filter(Boolean)
        .join("\n");

      await submitInquiry({
        name: form.name,
        email: `${form.phone.replace(/\D/g, "")}@curtains-lead.timefurniture.ae`,
        phone: form.phone,
        service: "Curtains & Blinds",
        service_tag: "curtains",
        source_page: "/curtains-dubai",
        subject: `Curtains & Blinds Quote · ${form.curtainStyle} · ${form.propertyType}`,
        message: composed,
        type: "curtains",
        property_type: form.propertyType,
        location: form.location,
      });
      toast.success("Thank you. Typical initial response within 30 minutes during working hours — a detailed quotation usually follows within 24 hours once measurements and specifications are confirmed.");
      track.formSubmitSuccess({ form: "curtains", service: "Curtains & Blinds", source_page: "/curtains-dubai" });
      setForm(INITIAL_FORM);
    } catch (err) {
      const msg =
        err?.response?.data?.detail ||
        "Something went wrong. Please WhatsApp us instead.";
      toast.error(typeof msg === "string" ? msg : "Please check your details.");
    } finally {
      setLoading(false);
    }
  };

  usePageMeta({
    title: "Curtains Dubai | Custom Curtains & Blinds UAE | Time Furniture",
    description:
      "Custom curtains, blinds and motorized Somfy & Silent Gliss systems designed and delivered across the UAE — 400+ fabrics, in-house stitching and expert install. Since 1993.",
    canonical: "https://timefurniture.ae/curtains-dubai",
    ogImage: "/media/revision-oct6/time-furniture-curtain-stitching-sewing-machine.webp",
    jsonLd: [
      videoJsonLd({
        key: "curtain-day-night",
        name: "Motorized curtains in Dubai — day to night design visualization",
        description:
          "Dubai bedroom design visualization — motorised sheer and blackout curtains moving through a full day: daylight opening, bedside lamp and cove lighting switching off for night, and a bright morning reopen.",
        seconds: 35,
      }),
      {
        "@context": "https://schema.org",
        "@type": "Service",
        serviceType: "Custom curtains, blinds and motorized curtain installation",
        name: "Curtains & Blinds — Dubai & Sharjah",
        provider: { "@id": "https://timefurniture.ae/#organization" },
        areaServed: [
          { "@type": "City", name: "Dubai" },
          { "@type": "City", name: "Sharjah" },
          { "@type": "Country", name: "United Arab Emirates" },
        ],
        hasOfferCatalog: {
          "@type": "OfferCatalog",
          name: "Curtain Collections",
          itemListElement: PRICING_TIERS.map((t) => ({
            "@type": "Offer",
            itemOffered: { "@type": "Service", name: t.name },
          })),
        },
      },
      {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: FAQS.map((f) => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
      },
    ],
  });

  return (
    <div data-testid="curtains-dubai-page" className="bg-[#FAF5EC]">
      <Toaster theme="light" position="top-center" />

      {/* ========== CINEMATIC HERO — curtain-day-night (Dubai design visualization) ========== */}
      <CinematicServiceHero
        mediaKey="curtain-day-night"
        mobileFullFrame
        posterAlt="Sheer and blackout curtains in a Dubai bedroom with a Downtown skyline view"
        eyebrow="Curtains & Blinds · Dubai & Sharjah"
        sectionNumber="01"
        testid="curtains-cinematic-hero"
        h1={
          <h1 className="font-serif-display font-medium text-white text-4xl sm:text-5xl md:text-6xl lg:text-7xl xl:text-[80px] leading-[1.06] tracking-[-0.015em] max-w-4xl">
            Custom Curtains &amp; Blinds in{" "}
            <em className="text-[#D4A963] not-italic">Dubai</em>.
          </h1>
        }
        support={
          <>
            <p className="mt-8 max-w-2xl text-white/85 leading-[1.7] text-base md:text-lg font-light">
              A trusted curtains and blinds company in Dubai — expert stitching,
              premium fabrics, and motorized curtain solutions tailored for your
              home or business.
            </p>
            <p className="mt-5 text-[#D4A963]/95 text-sm md:text-[15px] tracking-wide">
              Craftsmanship since 1993 — over 30 years of expertise in curtain stitching and installation.
            </p>
          </>
        }
      >
        <div className="mt-10 flex flex-wrap gap-4">
          <a
            href="#curtain-quote"
            data-testid="curtains-cta-quote"
            className="inline-flex items-center gap-3 bg-[#6E1F2C] text-white px-10 py-5 text-[11px] tracking-[0.32em] uppercase font-medium hover:bg-[#56151F] transition-all"
          >
            Get Curtain Quote <ArrowRight size={14} />
          </a>
          <a
            href={`https://wa.me/${COMPANY.whatsapp}?text=${COMPANY.whatsappCurtainsText}`}
            target="_blank"
            rel="noreferrer"
            data-testid="curtains-cta-whatsapp"
            className="inline-flex items-center gap-3 border border-[#D4A963]/60 text-white px-10 py-5 text-[11px] tracking-[0.32em] uppercase font-medium hover:bg-[#D4A963] hover:text-[#1C110C] transition-all"
          >
            <MessageCircle size={16} strokeWidth={2.2} /> Chat on WhatsApp
          </a>
        </div>
      </CinematicServiceHero>

      {/* ========== CURTAIN CRAFT — stitching at the sewing machine ========== */}
      <section data-testid="curtains-real-craft" className="bg-[#1C110C] border-b border-[#D4A963]/20">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-16 md:py-24 grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
          <figure>
            <img
              src="/media/revision-oct6/time-furniture-curtain-stitching-sewing-machine.webp"
              alt="Curtain craftsman guiding fabric through an industrial sewing machine"
              loading="lazy"
              decoding="async"
              width="1402"
              height="1122"
              className="w-full aspect-[5/4] object-cover"
            />
            <figcaption className="mt-3 text-[9px] tracking-[0.24em] uppercase text-[#D4A963]/80">
              In-house curtain stitching
            </figcaption>
          </figure>
          <div>
            <p className="text-[10px] tracking-[0.32em] uppercase text-[#D4A963]">The Hands Behind Every Curtain</p>
            <h2 className="mt-3 font-serif-display text-2xl md:text-4xl text-[#FAF5EC] leading-snug">
              Stitched in-house since 1993 — not outsourced.
            </h2>
            <p className="mt-4 text-sm md:text-base text-[#EDE1C8]/75 leading-[1.85]">
              Every curtain is measured on site, cut and stitched by our own master
              craftsmen at the Sharjah atelier, and installed by the same team.
              From the first seam to the final fitting, every detail is handled in-house.
            </p>
          </div>
        </div>
      </section>

      <Breadcrumbs
        testid="curtains-breadcrumbs"
        items={[
          { label: "Home", to: "/" },
          { label: "Services", to: "/services" },
          { label: "Curtains & Blinds" },
        ]}
      />

      {/* ========== TOP CONVERSION STRIP — "Quote in 30 Minutes" ========== */}
      <section
        data-testid="curtains-top-strip"
        className="bg-[#1C110C] text-[#FAF5EC] border-b border-[#D4A963]/30"
      >
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-5 md:py-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-5">
            <div className="md:flex md:items-baseline md:gap-6">
              <p className="font-serif-display text-base md:text-lg lg:text-xl text-[#FAF5EC] leading-tight">
                Custom Curtains in Dubai —{" "}
                <span className="text-[#D4A963]">Typical First Reply in 30 Minutes</span>.
              </p>
              <p className="mt-1 md:mt-0 text-[11px] md:text-[12px] tracking-[0.04em] text-[#EDE1C8]/80">
                WhatsApp us — typical initial response during working hours; detailed quotation usually within 24 hours once measurements are confirmed.
              </p>
            </div>
            <a
              href={`https://wa.me/${COMPANY.whatsapp}?text=${COMPANY.whatsappCurtainsText}`}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="curtains-top-strip-cta"
              className="inline-flex items-center justify-center gap-2.5 bg-[#D4A963] text-[#1C110C] px-6 py-3 text-[10px] md:text-[11px] tracking-[0.28em] uppercase font-medium hover:bg-[#c9a166] transition-all whitespace-nowrap self-start md:self-auto"
            >
              <MessageCircle size={14} strokeWidth={2.4} /> Get Quote on WhatsApp
            </a>
          </div>
        </div>
      </section>

      {/* ========== PRICING TIERS ========== */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-12 pt-10 md:pt-14 pb-24 md:pb-32">
        <div className="text-center max-w-3xl mx-auto mb-14 md:mb-20">
          <p className="eyebrow mb-5">Collection Options</p>
          <h2 className="font-serif-display text-4xl md:text-5xl lg:text-6xl tracking-tight leading-[1.05] text-[#1C110C]">
            Three collections for every space in <em className="text-[#6E1F2C] not-italic">Dubai</em>.
          </h2>
          <p className="mt-6 text-[#3E2A22] leading-relaxed text-base md:text-lg">
            Every order is quoted individually — your quotation depends on fabric choice,
            lining, stitching style, track or rod hardware, motorisation, window size and
            installation complexity. Individual rooms and single windows are as welcome as
            full villas and commercial projects.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
          {PRICING_TIERS.map((t, i) => (
            <article
              key={t.name}
              data-testid={`curtain-tier-${i}`}
              className={`relative flex flex-col p-9 md:p-10 ${
                t.featured
                  ? "bg-[#1C110C] text-[#FAF5EC] border-2 border-[#D4A963] md:scale-[1.04] md:-translate-y-3 shadow-[0_30px_60px_-15px_rgba(0,0,0,0.3)]"
                  : "bg-white border border-[#6E1F2C]/15"
              }`}
            >
              {t.featured && (
                <span className="absolute -top-4 left-1/2 -translate-x-1/2 bg-[#D4A963] text-[#1C110C] text-[10px] tracking-[0.32em] uppercase px-5 py-2 flex items-center gap-2 whitespace-nowrap shadow-[0_10px_30px_-10px_rgba(0,0,0,0.4)]">
                  <Star size={11} fill="currentColor" /> Most Chosen
                </span>
              )}
              <p
                className={`text-[10px] tracking-[0.32em] uppercase mb-3 ${
                  t.featured ? "text-[#D4A963]" : "text-[#6E1F2C]"
                }`}
              >
                {t.tag}
              </p>
              <h3 className={`font-serif-display text-3xl md:text-4xl ${t.featured ? "text-[#FAF5EC]" : "text-[#1C110C]"}`}>
                {t.name}
              </h3>
              <p className={`mt-3 italic text-sm ${t.featured ? "text-[#EDE1C8]" : "text-[#6E1F2C]"}`}>
                {t.blurb}
              </p>
              <ul className={`mt-7 space-y-3 text-sm ${t.featured ? "text-[#FAF5EC]" : "text-[#1C110C]"}`}>
                {t.points.map((p) => (
                  <li key={p} className="flex gap-3 items-start">
                    <Check
                      size={14}
                      strokeWidth={2.4}
                      className={`mt-1 shrink-0 ${t.featured ? "text-[#D4A963]" : "text-[#6E1F2C]"}`}
                    />
                    <span>{p}</span>
                  </li>
                ))}
              </ul>
            </article>
          ))}
        </div>

        <div className="mt-12 text-center">
          <a
            href="#curtain-quote"
            data-testid="curtains-cta-pricing"
            className="inline-flex items-center gap-3 bg-[#6E1F2C] text-white px-10 py-5 text-[11px] tracking-[0.32em] uppercase hover:bg-[#56151F] transition-all"
          >
            Request Pricing for Your Space →
          </a>
        </div>
      </section>

      {/* ========== CURTAIN ANATOMY — heading / fabric & layers / track & operation ========== */}
      <section data-testid="curtain-anatomy" className="border-b border-[#6E1F2C]/15 bg-[#FAF5EC]">
        <div className="max-w-[1500px] mx-auto px-6 md:px-12 py-16 md:py-20">
          <div className="max-w-3xl mb-10">
            <p className="eyebrow mb-5">Understand Your Curtain</p>
            <h2 className="font-serif-display text-3xl md:text-5xl tracking-tight leading-[1.05] text-[#1C110C]">
              Heading, fabric &amp; layers, track &amp; operation —{" "}
              <em className="text-[#6E1F2C] not-italic">three separate choices</em>.
            </h2>
            <p className="mt-5 text-[#3E2A22] leading-[1.8]">
              A curtain is not one decision. The <strong>heading</strong> is how the fabric is
              gathered at the top (ripple fold, pinch pleat). The <strong>fabric &amp; layers</strong>{" "}
              set light and privacy (sheer, blackout, double layer). The{" "}
              <strong>track &amp; operation</strong> is the hardware that carries it — M-track is a
              track system, not a pleat — and whether it moves by hand or motor.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {STYLE_GROUPS.map((g) => (
              <div key={g.id} data-testid={`anatomy-group-${g.id}`} className="bg-white border border-[#6E1F2C]/15 p-6">
                <p className="text-[10px] tracking-[0.28em] uppercase text-[#6E1F2C] font-medium mb-2">{g.label}</p>
                <p className="text-xs text-[#7A6B5F] leading-relaxed mb-4">{g.blurb}</p>
                <ul className="space-y-1.5">
                  {STYLE_GUIDES.filter((s) => s.group === g.id).map((s) => (
                    <li key={s.slug}>
                      <Link
                        to={`/curtains-dubai/styles/${s.slug}`}
                        data-testid={`anatomy-link-${s.slug}`}
                        className="text-sm text-[#1C110C] hover:text-[#6E1F2C] border-b border-transparent hover:border-[#6E1F2C]/40 transition-colors"
                      >
                        {s.name.split(" (")[0]} →
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ========== CURTAIN TYPES ========== */}
      <section className="bg-[#F2E9D8] border-y border-[#6E1F2C]/15">
        <div className="max-w-[1500px] mx-auto px-6 md:px-12 py-24 md:py-32">
          <div className="max-w-3xl mb-12 md:mb-16">
            <p className="eyebrow mb-5">Curtain Styles</p>
            <h2 className="font-serif-display text-4xl md:text-5xl lg:text-6xl tracking-tight leading-[1.05] text-[#1C110C]">
              Looking for curtains in Dubai?{" "}
              <em className="text-[#6E1F2C] not-italic">Six styles, endless fabrics.</em>
            </h2>
            <p className="mt-6 text-[#3E2A22] leading-[1.8] text-base md:text-[17px]">
              Whether you&rsquo;re fitting a Marina apartment, a Palm villa, or a
              Sharjah townhouse, these are the curtain styles our clients ask for
              most.
            </p>
          </div>
          <div
            data-testid="curtain-types-grid"
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4"
          >
            {CURTAIN_TYPES.map((c, i) => {
              const slug = NAME_TO_SLUG[c.name];
              const hasVideo = slug && Boolean((getMedia(`style-${slug}`) || {}).video);
              return (
                <Reveal key={c.name} delay={Math.min(i * 80, 240)}>
                  <figure className="group bg-white border border-[#6E1F2C]/15 hover:border-[#6E1F2C]/40 transition-colors overflow-hidden">
                    <div className="overflow-hidden">
                      <MotionMedia
                        mediaKey={slug ? `style-${slug}` : undefined}
                        posterOverride={c.img}
                        alt={c.alt}
                        mode="card"
                        aspectClass="aspect-[4/3]"
                        className="transition-transform duration-500 group-hover:scale-[1.02]"
                        testid={`card-media-${slug || c.name}`}
                      />
                    </div>
                    <figcaption className="p-5">
                      <p className="text-[10px] tracking-[0.28em] uppercase text-[#6E1F2C]/80 mb-1.5">
                        Curtain Style
                      </p>
                      <p className="font-serif-display text-xl md:text-2xl text-[#1C110C] leading-snug">
                        {c.name}
                      </p>
                      {slug && (
                        <Link
                          to={`/curtains-dubai/styles/${slug}`}
                          data-testid={`explore-style-${slug}`}
                          className="mt-2 inline-flex items-center gap-1.5 text-[10px] tracking-[0.24em] uppercase text-[#6E1F2C] border-b border-[#D4A963]/60 pb-0.5 hover:text-[#56151F] transition-colors"
                        >
                          Explore style →
                        </Link>
                      )}
                    </figcaption>
                  </figure>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* ========== BLINDS ========== */}
      <section className="max-w-[1500px] mx-auto px-6 md:px-12 py-24 md:py-32">
        <div className="max-w-3xl mb-12 md:mb-16">
          <p className="eyebrow mb-5">Blinds</p>
          <h2 className="font-serif-display text-4xl md:text-5xl lg:text-6xl tracking-tight leading-[1.05] text-[#1C110C]">
            Precision blinds for <em className="text-[#6E1F2C] not-italic">every window</em>.
          </h2>
        </div>
        <div
          data-testid="blinds-grid"
          className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4"
        >
          {BLINDS.map((b, i) => {
            const slug = NAME_TO_SLUG[b.name];
            const hasVideo = slug && Boolean((getMedia(`style-${slug}`) || {}).video);
            return (
              <Reveal key={b.name} delay={Math.min(i * 80, 240)}>
                <figure className="group bg-white border border-[#6E1F2C]/15 hover:border-[#6E1F2C]/40 transition-colors overflow-hidden">
                  <div className="overflow-hidden">
                    <MotionMedia
                      mediaKey={slug ? `style-${slug}` : undefined}
                      posterOverride={b.img}
                      alt={b.alt}
                      mode="card"
                      aspectClass="aspect-[4/3]"
                      className="transition-transform duration-500 group-hover:scale-[1.02]"
                      testid={`card-media-${slug || b.name}`}
                    />
                  </div>
                  <figcaption className="p-4">
                    <p className="text-[10px] tracking-[0.28em] uppercase text-[#6E1F2C]/80 mb-1.5">
                      Blinds
                    </p>
                    <p className="font-serif-display text-lg md:text-xl text-[#1C110C] leading-snug">
                      {b.name}
                    </p>
                    {slug && (
                      <Link
                        to={`/curtains-dubai/styles/${slug}`}
                        data-testid={`explore-style-${slug}`}
                        className="mt-2 inline-flex items-center gap-1.5 text-[10px] tracking-[0.24em] uppercase text-[#6E1F2C] border-b border-[#D4A963]/60 pb-0.5 hover:text-[#56151F] transition-colors"
                      >
                        Explore style →
                      </Link>
                    )}
                  </figcaption>
                </figure>
              </Reveal>
            );
          })}
        </div>
      </section>

      {/* ========== AUTHORITY · CURTAINS IN DUBAI — CUSTOM MADE & INSTALLED ========== */}
      <section
        data-testid="curtains-authority"
        className="bg-[#FAF5EC] border-y border-[#6E1F2C]/15"
      >
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-24 md:py-32 lg:py-40">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 mb-16 md:mb-20">
            <div className="lg:col-span-5">
              <p className="eyebrow mb-5">Curtains in Dubai</p>
              <h2 className="font-serif-display text-3xl md:text-5xl lg:text-[56px] tracking-tight leading-[1.05] text-[#1C110C]">
                Custom made &amp;{" "}
                <em className="text-[#6E1F2C] not-italic">installed</em>.
              </h2>
            </div>
            <div className="lg:col-span-7">
              <p className="text-[#3E2A22] leading-[1.85] text-base md:text-[17px]">
                Time Furniture provides custom-made curtains and blinds in
                Dubai, designed for apartments, villas, offices, and commercial
                interiors.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-12 lg:gap-16">
            {/* What we offer */}
            <div className="md:col-span-4">
              <p className="text-[10px] tracking-[0.32em] uppercase text-[#D4A963] mb-6">
                01 · What We Offer
              </p>
              <ul className="space-y-3 text-[#1C110C]">
                {[
                  "Sheer curtains",
                  "Blackout curtains",
                  "Ripple fold systems",
                  "Motorized curtains",
                  "Blinds (roller, zebra, roman)",
                ].map((it) => (
                  <li
                    key={it}
                    data-testid={`authority-offer-${it.split(" ")[0].toLowerCase()}`}
                    className="font-serif-display text-lg md:text-xl leading-snug border-b border-[#6E1F2C]/15 pb-3"
                  >
                    {it}
                  </li>
                ))}
              </ul>
            </div>

            {/* Our process */}
            <div className="md:col-span-4">
              <p className="text-[10px] tracking-[0.32em] uppercase text-[#D4A963] mb-6">
                02 · Our Process
              </p>
              <ol className="space-y-3 text-[#1C110C]">
                {[
                  "Site measurement",
                  "Fabric selection",
                  "In-house stitching",
                  "Professional installation",
                ].map((step, i) => (
                  <li
                    key={step}
                    data-testid={`authority-step-${i}`}
                    className="font-serif-display text-lg md:text-xl leading-snug border-b border-[#6E1F2C]/15 pb-3 flex items-baseline gap-3"
                  >
                    <span className="text-[#6E1F2C] text-sm">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    {step}
                  </li>
                ))}
              </ol>
            </div>

            {/* Service areas */}
            <div className="md:col-span-4">
              <p className="text-[10px] tracking-[0.32em] uppercase text-[#D4A963] mb-6">
                03 · Serving
              </p>
              <p className="text-[#3E2A22] leading-[1.85] text-base md:text-[17px]">
                Downtown Dubai, Dubai Marina, JVC, Palm Jumeirah, Business Bay,
                and across the UAE.
              </p>
              <p className="mt-6 text-sm text-[#7A6B5F]">
                Site visits and measurement appointments scheduled across all
                major Dubai and Sharjah neighbourhoods, on a same-week basis.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ========== MOTORIZED ========== */}
      <section className="bg-[#1C110C] text-[#FAF5EC]">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-24 md:py-32">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            <div className="lg:col-span-5">
              <p className="eyebrow mb-5 text-[#D4A963]">Smart Automation</p>
              <h2 className="font-serif-display text-4xl md:text-5xl lg:text-6xl tracking-tight leading-[1.05]">
                Motorized curtains &amp; <em className="text-[#D4A963] not-italic">smart automation</em>.
              </h2>
            </div>
            <div className="lg:col-span-7 text-[#E8DDC7] leading-[1.8] text-base md:text-[17px] space-y-6">
              <p>
                We design and install motorized curtain and blind systems built on
                premium motor platforms — <strong className="text-white">Somfy</strong> and{" "}
                <strong className="text-white">Silent Gliss</strong> — with remote, wall-switch,
                app and schedule control depending on the selected system.
              </p>
              <p data-testid="curtains-lutron-note">
                Where compatible, we integrate shades into{" "}
                <strong className="text-white">Lutron</strong> lighting scenes — for example via
                Lutron&rsquo;s dry-contact interfaces for compatible third-party shades — so curtains
                and lighting respond together. Not every motor connects to every control system:
                the correct motor and control interface is confirmed during specification.
              </p>
              <ul className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-4 text-[#FAF5EC]">
                {[
                  "Somfy & Silent Gliss premium motor options",
                  "Remote, wall switch, app & schedule control",
                  "Silent, smooth motor technology",
                  "Lutron scene integration where compatible — interface confirmed at specification",
                ].map((p) => (
                  <li key={p} className="flex gap-3 items-start">
                    <Check size={16} strokeWidth={2.4} className="mt-1 shrink-0 text-[#D4A963]" />
                    <span className="text-sm md:text-base">{p}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* ========== WHY CHOOSE ========== */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-12 py-24 md:py-32">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
            <p className="eyebrow mb-5">Why Time Furniture</p>
            <h2 className="font-serif-display text-4xl md:text-5xl tracking-tight leading-[1.05] text-[#1C110C]">
              Real fabric. <em className="text-[#6E1F2C] not-italic">Real craft.</em>
            </h2>
            <p className="mt-6 text-[#3E2A22] leading-[1.85] text-sm md:text-base">
              When clients search for the best curtain company in Dubai, they
              come to us for one reason: every curtain is measured, stitched and
              installed by the same team — start to finish.
            </p>
          </div>
          <div className="lg:col-span-8 grid grid-cols-1 md:grid-cols-2 gap-px bg-[#6E1F2C]/15">
            {WHY_CHOOSE.map((w) => (
              <div key={w} className="bg-[#FAF5EC] p-7 flex gap-4 items-start">
                <Check size={18} strokeWidth={2.4} className="mt-1 shrink-0 text-[#6E1F2C]" />
                <span className="text-[#1C110C] text-sm md:text-base">{w}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ========== TRUST QUOTE ========== */}
      <section className="bg-[#F2E9D8] border-y border-[#6E1F2C]/15">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-24 md:py-32">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-center">
            <div className="lg:col-span-5 order-2 lg:order-1">
              <div className="overflow-hidden">
                <img
                  src="/media/revision-oct6/time-furniture-curtain-stitching-sewing-machine.webp"
                  alt="Curtain craftsman guiding fabric through an industrial sewing machine"
                  loading="lazy"
              decoding="async"
              width="1402"
              height="1122"
                  className="w-full aspect-[5/4] object-cover"
                />
              </div>
            </div>
            <div className="lg:col-span-7 order-1 lg:order-2">
              <p className="eyebrow mb-6">Real Team</p>
              <h2 className="font-serif-display text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-[#1C110C]">
                Real Craftsmanship. <em className="text-[#6E1F2C] not-italic">Real Team.</em>
              </h2>
              <p className="mt-8 text-[#3E2A22] leading-[1.8] text-base md:text-[17px] max-w-xl">
                Every curtain is measured, stitched and installed by our own team. No middlemen, no
                outsourced workshops — just craftsmen who&rsquo;ve been with Time Furniture for
                decades.
              </p>
              <p className="mt-6 text-sm md:text-base text-[#7A6B5F] max-w-xl">
                Curtains pair beautifully with our in-house atelier work —{" "}
                <Link
                  to="/services/upholstery"
                  data-testid="curtains-link-upholstery"
                  className="text-[#6E1F2C] underline underline-offset-4 hover:text-[#56151F]"
                >
                  view our upholstery services
                </Link>
                .
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ========== QUICK QUOTE STRIP — 30-min promise ========== */}
      <QuickQuoteStrip
        testid="curtains-quick-quote"
        prefilled="Hi I am looking for curtains. Can you help me with a quote?"
        useUnifiedLink={true}
        variant="burgundy"
      />

      {/* ========== INQUIRY FORM ========== */}
      <section
        id="curtain-quote"
        data-testid="curtain-quote-section"
        className="max-w-[1100px] mx-auto px-6 md:px-12 py-24 md:py-32"
      >
        <div className="text-center max-w-2xl mx-auto mb-12">
          <p className="eyebrow mb-5">Request Quote</p>
          <h2 className="font-serif-display text-4xl md:text-5xl tracking-tight leading-[1.05] text-[#1C110C]">
            Get a custom <em className="text-[#6E1F2C] not-italic">curtain quote</em>.
          </h2>
          <p className="mt-5 text-[#7A6B5F] text-base">
            Just your name, phone, area and what you need — individual rooms are as
            welcome as full villas. Typical initial response within 30 minutes during
            working hours; a detailed quotation is usually prepared within 24 hours
            once measurements and specifications are confirmed.
          </p>
        </div>

        <form
          onSubmit={onSubmit}
          data-testid="curtain-form"
          className="bg-white border border-[#6E1F2C]/15 p-7 md:p-10 grid grid-cols-1 md:grid-cols-2 gap-5"
        >
          <Field testid="cf-name" label="Name *" value={form.name} onChange={(v) => update("name", v)} />
          <Field testid="cf-phone" label="Phone *" value={form.phone} onChange={(v) => update("phone", v)} type="tel" placeholder="+971..." />
          <Field testid="cf-location" label="Area" value={form.location} onChange={(v) => update("location", v)} placeholder="Dubai / Sharjah / Area" />

          <Select testid="cf-type" label="Curtains or Blinds" value={form.type} onChange={(v) => update("type", v)} options={["Curtains", "Blinds", "Both"]} />

          <div className="md:col-span-2 pt-1">
            <button
              type="button"
              onClick={() => setShowDetails((s) => !s)}
              aria-expanded={showDetails}
              data-testid="cf-toggle-details"
              className="inline-flex items-center gap-2 text-[11px] tracking-[0.24em] uppercase text-[#6E1F2C] hover:text-[#56151F] border-b border-[#6E1F2C]/30 pb-1 transition-colors"
            >
              {showDetails ? <Minus size={13} /> : <Plus size={13} />}
              Add more details <span className="normal-case tracking-normal text-[#B8904E]/80">(optional)</span>
            </button>
            <p className="mt-2 text-xs text-[#7A6B5F]">
              Not sure about sizes? No problem — we include help with measurement on a free site visit.
            </p>
          </div>

          {showDetails && (
            <>
              <Select
                testid="cf-style"
                label="Curtain / Blind Type"
                value={form.curtainStyle}
                onChange={(v) => update("curtainStyle", v)}
                options={PRODUCT_TYPE_OPTIONS}
              />

              <Select testid="cf-property" label="Property Type" value={form.propertyType} onChange={(v) => update("propertyType", v)} options={["Residential", "Commercial"]} />

              <Field testid="cf-windows" label="Number of Windows" value={form.numWindows} onChange={(v) => update("numWindows", v)} type="number" placeholder="e.g. 6" />

              <Select
                testid="cf-size-known"
                label="Measurements"
                value={form.knowsSize}
                onChange={(v) => update("knowsSize", v)}
                options={["Need help with measurement (free site visit)", "I know my sizes"]}
              />

              {form.knowsSize === "I know my sizes" && (
                <>
                  <Field testid="cf-height" label="Height (cm)" value={form.height} onChange={(v) => update("height", v)} type="number" optional placeholder="e.g. 280" />

                  <Field testid="cf-width" label="Width (cm)" value={form.width} onChange={(v) => update("width", v)} type="number" optional placeholder="e.g. 220" />
                </>
              )}

              <Select testid="cf-automation" label="Automation Required?" value={form.automation} onChange={(v) => update("automation", v)} options={["No", "Yes"]} />

              <div className="md:col-span-2">
                <Field testid="cf-message" label="Anything else we should know?" value={form.message} onChange={(v) => update("message", v)} textarea optional />
              </div>
            </>
          )}

          <div className="md:col-span-2 flex flex-wrap items-center justify-between gap-5 pt-4">
            <p className="text-xs text-[#7A6B5F]">
              By submitting, you agree to be contacted by Time Furniture.
            </p>
            <button
              type="submit"
              disabled={loading}
              data-testid="cf-submit"
              className="inline-flex items-center gap-3 bg-[#6E1F2C] text-white px-10 py-5 text-[11px] tracking-[0.32em] uppercase hover:bg-[#56151F] transition-all disabled:opacity-60"
            >
              {loading ? "Sending…" : "Get Curtain Quote"} <ArrowRight size={14} />
            </button>
          </div>
        </form>
      </section>

      {/* ========== PROCESS — Consultation → Handover ========== */}
      <section
        data-testid="curtains-process"
        className="max-w-[1400px] mx-auto px-6 md:px-12 py-20 md:py-28"
      >
        <p className="eyebrow mb-5">From brief to install</p>
        <h2 className="font-serif-display text-3xl md:text-5xl tracking-tight leading-[1.05] text-[#1C110C] max-w-3xl">
          A clean five-step process.
        </h2>
        <p className="mt-6 max-w-2xl text-[#3E2A22] leading-[1.85] text-base md:text-[17px] font-light">
          Most curtain projects in Dubai are completed within 2–3 weeks of
          measurement, fabrication and install — managed by a single project
          lead from start to finish.
        </p>
        <ol className="mt-12 space-y-10">
          {[
            {
              n: "01",
              t: "Consultation",
              d: "We listen to your brief — light control, privacy, acoustics, smart-home goals — and recommend curtain types, blinds and motorisation options that suit how you actually use the room.",
            },
            {
              n: "02",
              t: "Design & planning",
              d: "On-site visit, fabric library, hardware selection (manual vs motorised, side-cord vs touch), pelmet/track/rod options and an itemised quote — fabric, stitching and install priced separately.",
            },
            {
              n: "03",
              t: "Material selection",
              d: "Physical fabric samples reviewed in your own light. Linings, blackout layers and motor specifications confirmed before any cutting begins.",
            },
            {
              n: "04",
              t: "Execution",
              d: "Curtains stitched at our own Sharjah curtain master atelier; tracks, rods and motors prepared in parallel. Quality-checked before they leave the workshop.",
            },
            {
              n: "05",
              t: "Handover",
              d: "On-site install, motor pairing and testing, snag walkthrough, and care notes. Backed by a written defects warranty on the fabrication and install.",
            },
          ].map((s) => (
            <li
              key={s.n}
              data-testid={`curtains-process-step-${s.n}`}
              className="grid grid-cols-12 gap-6 md:gap-10 border-t border-[#6E1F2C]/15 pt-8"
            >
              <p className="col-span-2 md:col-span-1 font-serif-display text-2xl md:text-3xl text-[#D4A963]">
                {s.n}
              </p>
              <div className="col-span-10 md:col-span-11">
                <h3 className="font-serif-display text-xl md:text-2xl text-[#1C110C] mb-3">
                  {s.t}
                </h3>
                <p className="text-[#3E2A22] leading-[1.85] text-base md:text-[17px] max-w-2xl">
                  {s.d}
                </p>
              </div>
            </li>
          ))}
        </ol>
      </section>

      {/* ========== WHY CHOOSE TIME FURNITURE ========== */}
      <section
        data-testid="curtains-why-choose"
        className="bg-[#FAF5EC] border-y border-[#6E1F2C]/15"
      >
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-20 md:py-28">
          <p className="eyebrow mb-5">Why Time Furniture</p>
          <h2 className="font-serif-display text-3xl md:text-5xl tracking-tight leading-[1.05] text-[#1C110C] max-w-3xl">
            Curtains backed by 30+ years of fit-out delivery.
          </h2>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-10">
            {[
              {
                t: "UAE since 1993",
                d: "Three decades delivering interior fit-out and soft furnishings across Dubai, Sharjah and the wider UAE.",
              },
              {
                t: "In-house manufacturing",
                d: "Curtains stitched at our own Sharjah curtain-master atelier — full control over quality and lead time.",
              },
              {
                t: "Turnkey execution",
                d: "Measurement, supply, motorisation and install handled by a single project lead — no third-party finger-pointing.",
              },
              {
                t: "Premium materials",
                d: "Designer fabrics, blackout linings, smart-home motor systems and engineered tracks — selected to last.",
              },
              {
                t: "End-to-end responsibility",
                d: "Written defects warranty on the fabrication and install. Adjustment visits in the first year.",
              },
              {
                t: "2,300+ projects delivered",
                d: "Villas, apartments, hospitality, holiday homes and commercial spaces across the UAE.",
              },
            ].map((w, i) => (
              <div
                key={w.t}
                data-testid={`curtains-why-${i}`}
                className="border-t border-[#6E1F2C]/20 pt-6"
              >
                <h3 className="font-serif-display text-xl text-[#1C110C] mb-3">
                  {w.t}
                </h3>
                <p className="text-sm md:text-base text-[#3E2A22] leading-[1.75] font-light">
                  {w.d}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ========== AISO BLOCK — AI-search optimised entity content ========== */}
      <AISOBlock
        testid="curtains-aiso"
        audienceHeading="From a single window to a full home — every project is welcome."
        audience={[
          "Single-window and single-room orders — bedrooms, majlis, living rooms",
          "Villa owners furnishing or upgrading curtain and blind systems",
          "Apartment renovation clients across Dubai, Marina, Downtown, Hills",
          "Holiday home and Airbnb investors needing fast, durable installs",
          "Commercial and hospitality clients (offices, clinics, lounges)",
        ]}
        whatWeDo="We design, manufacture and install custom curtains, blinds and motorised window treatments across Dubai and the wider UAE. From sheer and ripple-fold curtains in apartments to blackout, pinch-pleat and smart-home-integrated motorised systems in villas — every order is laser-measured on site, stitched at our Sharjah curtain-master atelier and installed by our own team."
        scope={[
          "On-site measurement & fabric consultation",
          "Curtains: ripple-fold, pinch-pleat, sheer, blackout, double-layer",
          "Blinds: roller, roman, venetian, motorised",
          "Tracks, rods, pelmets & motorisation",
          "Smart-home motor integration",
          "On-site install, snag & after-care",
        ]}
        relatedLinks={[
          { kicker: "Turnkey", label: "Interior Fit-Out Dubai", to: "/interior-fit-out-dubai" },
          { kicker: "Upholstery", label: "Custom Sofas & Upholstery", to: "/custom-sofas-dubai" },
          { kicker: "Renovation", label: "Villa Renovation Dubai", to: "/villa-renovation-dubai" },
          { kicker: "All Services", label: "Full Service Range", to: "/services" },
        ]}
      />

      {/* ========== FAQ (single consolidated, schema matches visible answers) ========== */}
      <section
        data-testid="curtains-faq"
        className="bg-[#FAF5EC] border-t border-[#6E1F2C]/10"
      >
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-24 md:py-32">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
            <div className="lg:col-span-4">
              <p className="eyebrow mb-5">Common Questions</p>
              <h2 className="font-serif-display text-4xl md:text-5xl lg:text-6xl tracking-tight leading-[1.02] text-[#1C110C]">
                Frequently <em className="text-[#6E1F2C] not-italic">asked</em>.
              </h2>
            </div>
            <div className="lg:col-span-8">
              <div className="border-t border-[#6E1F2C]/15">
                {FAQS.map((item, i) => {
                  const isOpen = openFaq === i;
                  return (
                    <div
                      key={item.q}
                      className="border-b border-[#6E1F2C]/15"
                      data-testid={`curtain-faq-item-${i}`}
                    >
                      <button
                        onClick={() => setOpenFaq(isOpen ? -1 : i)}
                        aria-expanded={isOpen}
                        className="w-full flex items-start justify-between gap-6 py-6 md:py-8 text-left group"
                      >
                        <h3 className="font-serif-display text-xl md:text-2xl text-[#1C110C] leading-snug pr-4">
                          {item.q}
                        </h3>
                        <span className="shrink-0 mt-1 text-[#6E1F2C] group-hover:text-[#56151F]">
                          {isOpen ? <Minus size={22} strokeWidth={1.6} /> : <Plus size={22} strokeWidth={1.6} />}
                        </span>
                      </button>
                      {isOpen && (
                        <div className="pb-7 md:pb-9 pr-12 text-[#3E2A22] leading-[1.8] text-[15px] md:text-base">
                          {item.a}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ========== FINAL CTA ========== */}
      <section
        data-testid="curtains-final-cta"
        className="relative bg-[#1C110C] text-[#FAF5EC] overflow-hidden"
      >
        {/* Subtle gold accent line at top */}
        <div className="h-[2px] w-full bg-gradient-to-r from-transparent via-[#D4A963] to-transparent" />

        {/* Faint texture for depth */}
        <div
          aria-hidden
          className="absolute inset-0 opacity-[0.04] pointer-events-none"
          style={{
            backgroundImage:
              "radial-gradient(circle at 20% 20%, #D4A963 0%, transparent 50%), radial-gradient(circle at 80% 80%, #6E1F2C 0%, transparent 50%)",
          }}
        />

        <div className="relative max-w-[1200px] mx-auto px-6 md:px-12 py-24 md:py-32 lg:py-40 text-center">
          <p className="text-[10px] md:text-[11px] tracking-[0.36em] uppercase text-[#D4A963] mb-6 font-medium">
            Limited slots this week · Book a free site visit
          </p>

          <h2 className="font-serif-display text-3xl md:text-5xl lg:text-[60px] tracking-tight leading-[1.08] text-[#FAF5EC] max-w-4xl mx-auto">
            Ready to upgrade your space with{" "}
            <em className="text-[#D4A963] not-italic">custom curtains</em>?
          </h2>

          <p className="mt-7 max-w-xl mx-auto text-[#EDE1C8]/85 leading-[1.7] text-base md:text-lg">
            Book a free site visit anywhere in Dubai or UAE.
          </p>

          <div className="mt-12 flex flex-col sm:flex-row gap-4 justify-center items-center">
            <Link
              to="/curtains-dubai#curtain-quote"
              data-testid="curtains-cta-final-quote"
              className="inline-flex items-center justify-center gap-3 bg-[#D4A963] text-[#1C110C] px-10 py-5 text-[11px] tracking-[0.32em] uppercase font-medium hover:bg-[#c9a166] transition-all"
            >
              Get Curtain Quote →
            </Link>
            <a
              href={`tel:${COMPANY.phone.replace(/\s/g, "")}`}
              data-testid="curtains-cta-final-call"
              className="inline-flex items-center justify-center gap-3 border border-[#D4A963]/60 text-[#FAF5EC] px-10 py-5 text-[11px] tracking-[0.32em] uppercase hover:bg-[#D4A963] hover:text-[#1C110C] hover:border-[#D4A963] transition-all"
            >
              <Phone size={15} strokeWidth={2.2} /> Call Now
            </a>
          </div>

          <p className="mt-10 text-xs tracking-[0.04em] text-[#EDE1C8]/60">
            Typical initial response within 30 minutes during working hours · Detailed quotation usually within 24 hours once measurements are confirmed · Crafted since 1993
          </p>
        </div>
      </section>
    </div>
  );
}

/* ---------- Form helpers ---------- */
function Field({ testid, label, value, onChange, type = "text", placeholder, textarea, optional }) {
  const Tag = textarea ? "textarea" : "input";
  return (
    <label className="block">
      <span className="text-[10px] tracking-[0.24em] uppercase text-[#7A6B5F] mb-2 block">
        {label}
        {optional && <span className="ml-2 text-[#B8904E]/70 normal-case tracking-normal">optional</span>}
      </span>
      <Tag
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        data-testid={testid}
        rows={textarea ? 4 : undefined}
        className="w-full bg-[#FAF5EC] border border-[#6E1F2C]/20 px-4 py-3 text-sm text-[#1C110C] focus:border-[#6E1F2C] focus:outline-none focus:ring-1 focus:ring-[#6E1F2C]/30 transition"
      />
    </label>
  );
}

function Select({ testid, label, value, onChange, options }) {
  return (
    <label className="block">
      <span className="text-[10px] tracking-[0.24em] uppercase text-[#7A6B5F] mb-2 block">{label}</span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        className="w-full bg-[#FAF5EC] border border-[#6E1F2C]/20 px-4 py-3 text-sm text-[#1C110C] focus:border-[#6E1F2C] focus:outline-none focus:ring-1 focus:ring-[#6E1F2C]/30 transition"
      >
        {options.map((o) => (
          <option key={o} value={o}>
            {o}
          </option>
        ))}
      </select>
    </label>
  );
}
